import React, { forwardRef } from 'react';
import { AlertOctagon, CheckCircle2, ShieldAlert, Sparkles, QrCode } from 'lucide-react';
import { QuizResult } from '../types';

interface ResultCardProps {
  result: QuizResult;
}

export const ResultCard = forwardRef<HTMLDivElement, ResultCardProps>(({ result }, ref) => {
  const isDanger = result.badgeType === 'danger';

  return (
    <div
      ref={ref}
      id="result-poster-card"
      className="w-full bg-[#FAF7F0] border-3 border-ink-950 p-5 sm:p-6 shadow-dossier-lg relative overflow-hidden text-ink-900 select-none font-sans"
    >
      {/* 档案背景纹理线与角标 */}
      <div className="absolute top-2 left-2 text-[9px] font-mono text-ink-400 select-none">
        FORM NO. BIO-2026-X
      </div>
      <div className="absolute top-2 right-2 text-[9px] font-mono text-ink-400 select-none">
        EXP_STATUS: FINAL
      </div>

      {/* 1. 顶部档案头：机密鉴定书 Header */}
      <div className="border-b-2 border-ink-950 pb-3 pt-2 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 font-mono text-xs font-black tracking-wider uppercase text-ink-950">
            <span className={`w-2.5 h-2.5 inline-block ${isDanger ? 'bg-stamp animate-pulse' : 'bg-lab-green'}`} />
            <span>BIO-ANOMALY EVALUATION // 终身伴侣物种裁决</span>
          </div>
          <span className={`font-mono text-[10px] font-bold px-1.5 py-0.5 border ${
            isDanger ? 'border-stamp text-stamp bg-stamp-light' : 'border-ink-700 text-ink-800 bg-paper-200'
          }`}>
            {result.caseId}
          </span>
        </div>

        <div className="flex justify-between items-baseline mt-1 text-[11px] font-mono text-ink-500">
          <span>{result.confidentialLevel}</span>
          <span className="font-bold text-ink-900">综合适配度：<strong className={`text-sm ${isDanger ? 'text-stamp' : 'text-lab-green'}`}>{result.matchRate}</strong></span>
        </div>
      </div>

      {/* 2. 结果标题与判定等级 */}
      <div className="space-y-1.5 mb-4">
        <div className="flex items-center justify-between">
          <span className={`inline-block px-2 py-0.5 text-[10px] font-mono font-bold tracking-wider uppercase border ${
            isDanger
              ? 'bg-stamp text-paper-50 border-stamp'
              : 'bg-ink-900 text-paper-50 border-ink-900'
          }`}>
            {result.categoryBadge}
          </span>
          <span className="font-mono text-xs text-ink-400 font-bold">#2026-FINAL</span>
        </div>

        <div className="pt-1">
          <div className="text-xs font-mono text-ink-500 uppercase tracking-wider">
            SYSTEM DIRECTIVE // 系统最终裁定物种：
          </div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-ink-950 mt-0.5 flex items-center gap-2">
            <span>{result.targetName}</span>
          </h1>
          <p className="text-xs font-mono font-medium text-ink-600 mt-1">
            {result.subtitle}
          </p>
        </div>
      </div>

      {/* 3. 核心大图展示区 (Hero Image: 干净背景、单主体、低多边形插画感) */}
      <div className="my-4 relative">
        <div className="w-full bg-white border-2 border-ink-950 p-3 shadow-sharp-sm relative overflow-hidden flex flex-col items-center">
          {/* 标本视图水印说明 */}
          <div className="w-full flex justify-between items-center text-[9px] font-mono text-ink-400 border-b border-ink-200 pb-1.5 mb-2">
            <span>标本渲染视图 // 3D 低多边形</span>
            <span>比例: 1:1 // 等轴测视图</span>
          </div>

          {/* 大图主体 */}
          <div className="w-full max-w-[280px] aspect-square flex items-center justify-center relative py-2">
            <img
              src={result.imageUrl}
              alt={result.imageAlt}
              className="w-full h-full object-contain filter drop-shadow-md transition-transform duration-300 hover:scale-105"
            />
          </div>

          {/* 底部物种状态注记 */}
          <div className="w-full pt-1.5 border-t border-ink-100 flex justify-between items-center text-[10px] font-mono text-ink-600">
            <span className="truncate font-bold">物种名称: {result.targetName}</span>
            <span className="font-bold text-ink-900">存活状态: 活跃</span>
          </div>
        </div>

        {/* 覆盖在图片右上角的红色印章 (盖印效果) */}
        <div className="absolute -top-3 -right-2 pointer-events-none z-20">
          <div className={`px-2.5 py-1 border-2 font-black font-mono text-xs uppercase tracking-widest rotate-[12deg] shadow-xs ${
            isDanger
              ? 'border-stamp text-stamp bg-white/90 ring-1 ring-stamp/40'
              : 'border-lab-blue text-lab-blue bg-white/90'
          }`}>
            {result.stampText}
          </div>
        </div>
      </div>

      {/* 4. 8 项统一生活方式匹配刻度 (LIFESTYLE PROFILE) —— 手机优先单列纵向阅读 */}
      <div className="space-y-4 mb-4 border-2 border-ink-950 bg-white p-4 sm:p-5 shadow-sharp-sm">
        <div className="flex justify-between items-center text-xs font-mono font-bold text-ink-900 border-b border-ink-200 pb-2">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 bg-ink-950 inline-block" />
            <span>LIFESTYLE PROFILE // 生活方式匹配刻度</span>
          </span>
          <span className="text-ink-400 font-normal shrink-0 whitespace-nowrap">8-DIM</span>
        </div>

        <div className="space-y-5 pt-1">
          {result.metrics.map((m, idx) => (
            <div key={idx} className="space-y-1.5">
              <div className="flex justify-between items-baseline gap-3 font-mono">
                <div className="flex items-baseline gap-2 min-w-0">
                  <span className="text-base font-bold text-ink-900 leading-tight">
                    {m.label}
                  </span>
                  {m.subLabel && (
                    <span className="text-[11px] font-normal text-ink-400 uppercase tracking-wider truncate">
                      {m.subLabel}
                    </span>
                  )}
                </div>
                <span className="font-black text-ink-900 font-mono text-base shrink-0">
                  {m.value}%
                </span>
              </div>
              <div className="w-full h-2.5 bg-paper-200 border border-ink-400 p-[1px]">
                <div
                  className={`h-full transition-all duration-300 ${
                    isDanger ? 'bg-stamp' : 'bg-lab-blue'
                  }`}
                  style={{ width: `${m.value}%` }}
                />
              </div>
              {m.note && (
                <div className="text-[13px] font-mono text-ink-600 leading-relaxed pt-0.5">
                  {m.note}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* 5. 调整后次序：系统评语与荒诞长文解析 (指标之后给出详细原因) */}
      <div className="space-y-2 mb-4 bg-paper-100 border border-ink-300 p-3.5 text-xs text-ink-800">
        <div className="flex items-center gap-1 font-mono font-bold text-ink-950 text-[11px] pb-1 border-b border-ink-200">
          <AlertOctagon className="w-3.5 h-3.5 text-ink-700" />
          <span>【中枢综合判定报告书】</span>
        </div>

        <p className="whitespace-pre-line leading-relaxed font-medium text-ink-900 text-[12px]">
          {result.systemVerdict}
        </p>

        <div className="space-y-1.5 pt-2 border-t border-ink-200/80 text-[11px] text-ink-700">
          {result.detailedAnalysis.map((item, idx) => (
            <p key={idx} className="leading-snug">
              {item}
            </p>
          ))}
        </div>
      </div>

      {/* 6. 饲养忠告警示 */}
      <div className={`p-2.5 border text-xs font-mono mb-4 ${
        isDanger
          ? 'bg-stamp-light/70 border-stamp/70 text-stamp-dark'
          : 'bg-paper-200 border-ink-400 text-ink-800'
      }`}>
        {result.coexistenceAdvice}
      </div>

      {/* 7. 底部小红书专属防伪条码、存根与弱化娱乐声明 */}
      <div className="border-t-2 border-dashed border-ink-400 pt-3 space-y-2 text-ink-500">
        <div className="flex items-center justify-between text-[10px] font-mono">
          <div className="space-y-0.5">
            <div className="font-bold text-ink-800 tracking-wider">
              小红书搜「这也要测」
            </div>
            <div>中央收容研究所 · 官方存根认证</div>
          </div>
          <div className="flex items-center gap-1.5 font-mono text-[9px] border border-ink-400 px-2 py-1 bg-paper-100">
            <span>VERIFIED: 2026</span>
          </div>
        </div>

        {/* 弱化娱乐声明 */}
        <p className="text-center text-[10px] font-mono text-ink-400/90 pt-1 border-t border-ink-200/50">
          测试结果纯属娱乐，请勿作为真实饲养决策依据。
        </p>
      </div>
    </div>
  );
});

ResultCard.displayName = 'ResultCard';
